'use client';

import { useState } from 'react';
import { cn } from '@/lib/utils';
import Image from './image';
import {
  AVATAR_HUE_CLASSES,
  AVATAR_PX,
  AVATAR_SIZE_CLASSES,
  avatarHue,
  avatarInitials,
  type AvatarSize,
} from './avatarCore';

export interface AvatarProps {
  name: string;
  /** Photo; when missing or failing to load the initials show on the name's hue pair. */
  photoUrl?: string | null;
  size?: AvatarSize;
  /** Default true: the name sits next to the avatar, so it is hidden from assistive tech. */
  decorative?: boolean;
  className?: string;
}

/** Initials avatar on one of six name-hashed hue pairs, or the person's photo. */
export function Avatar({ name, photoUrl, size = 'md', decorative = true, className }: AvatarProps) {
  const [failed, setFailed] = useState(false);
  const showPhoto = !!photoUrl && !failed;
  const px = AVATAR_PX[size];
  return (
    <span
      data-avatar
      data-hue={showPhoto ? undefined : avatarHue(name)}
      aria-hidden={decorative || undefined}
      role={decorative ? undefined : 'img'}
      aria-label={decorative ? undefined : name}
      title={name}
      className={cn(
        'relative inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full font-mono font-semibold',
        AVATAR_SIZE_CLASSES[size],
        showPhoto ? 'bg-surface' : AVATAR_HUE_CLASSES[avatarHue(name)],
        className,
      )}
    >
      {showPhoto ? (
        <Image
          src={photoUrl}
          alt=""
          width={px * 2}
          height={px * 2}
          sizes={`${px}px`}
          className="absolute inset-0 h-full w-full object-cover"
          onError={() => setFailed(true)}
        />
      ) : (
        avatarInitials(name)
      )}
    </span>
  );
}
